import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activityWord,
  EXPORTED_FILES_AFTER_ORIGINAL_DELETE,
  formatDateTime,
  formatKrw,
  isResultReceiveClosed,
  needsYear,
  RESULT_RECEIVE_EXPIRED_COPY,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { Button, Skeleton } from "../components/primitives";
import { LimitBanner } from "../components/banners";
import { ConfirmDialog, Toast } from "../components/feedback";
import { fetchJobEvents, fetchJobVersions, versionBundleUrl } from "../lib/api";
import { exportDeclineView } from "../lib/export-decline-web";
import type { ShellJob } from "../lib/shell-job";
import { useJobView } from "../lib/use-job-view";
import {
  deleteErrorMessage,
  deleteJobOriginals,
  deleteResultMessage,
  fetchJobOriginals,
  originalsLine,
  REVERT_ALL_PARTS,
  revertErrorMessage,
  revertResultMessage,
  revertToVersion,
} from "../lib/originals-api";

type Dialog = { kind: "revert"; version: string; versionId: string } | { kind: "delete"; count: number } | null;

/** 타임라인 한 줄. 서버 줄을 화면 모양으로 맞춘다. */
interface TimelineRow {
  key: string;
  when: string;
  what: string;
  /** 돈이 나간 줄의 금액 배지. */
  cost?: string;
  who: string;
  accent: boolean;
}

/** 판 한 줄. `bundleHref` 가 있으면 `다시 받기` 가 실제로 받는다. */
interface VersionRow {
  key: string;
  versionId: string;
  name: string;
  at: string;
  sub: string;
  current: boolean;
  bundleHref?: string;
}

/** `W-50` 한 작업의 이력. **작업 셸의 서브탭이 아니라 별도 화면이다.**
 *
 * 작업 바의 `이력 ›`으로 들어오고 `‹ 검토 · 고치기`로 돌아간다. 앱의 `A-12` 도 탭바에 없고
 * 흐름 화면의 서브탭도 아니며 홈에서 행을 눌러 들어가는 별도 화면이다 — **웹만 이력을 탭으로
 * 들고 있던 것을 맞췄다**(정본 목업 `W-50`).
 *
 * 상품명·썸네일·판 번호는 셸이 그린다. 이 화면의 제목 줄에는 **이 화면의 총량**을 둔다 —
 * 같은 값을 한 화면에 두 번 적지 않는다. */
export function HistoryScreen() {
  const job = useOutletContext<ShellJob | undefined>();
  const { view } = useJobView(job?.id);
  /** 작업이 있으면 실제 이력·판을 부른다.
   *
   * ⛔ **예시 작업 갈래를 걷었다** (2026-10-04 사용자 지시). 예시 이력(`SAMPLE_TIMELINE` ·
   * `SAMPLE_VERSIONS`)은 남의 상품의 가짜 줄이었다. 이제 예시 작업 자체가 없다
   * (2026-10-05 에 `findSampleJob` 도 걷었다). */
  const real = Boolean(job);

  const [dialog, setDialog] = useState<Dialog>(null);
  const [toast, setToast] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // 무효화 키는 결과 확인(`job-events`)·내보내기(`job-versions`)가 쓰는 것과 같다. 승인하거나
  // 내보낸 뒤 이 화면으로 오면 새 줄과 새 판이 그대로 보인다.
  const eventsQuery = useQuery({
    queryKey: ["job-events", job?.id],
    queryFn: () => fetchJobEvents(job!.id),
    enabled: real,
  });
  const versionsQuery = useQuery({
    queryKey: ["job-versions", job?.id],
    queryFn: () => fetchJobVersions(job!.id),
    enabled: real,
  });
  /** 원본 현황(`원본 n장 · n일 남음`). 남은 날은 서버가 셈한 `daysLeft` 그대로다. 화면이 다시 세지 않는다. */
  const originalsQuery = useQuery({
    queryKey: ["job-originals", job?.id],
    queryFn: () => fetchJobOriginals(job!.id),
    enabled: real,
  });

  /** `null` 이면 알림 없이 닫기만 한다(401: 세션 게이트가 로그인으로 넘긴다). */
  function notify(message: string | null) {
    setDialog(null);
    if (message === null) return;
    setToast(message);
    window.setTimeout(() => setToast(null), 5000);
  }

  // 어느 결과든 끝나면 현황을 다시 부른다. 일부만 지웠거나 응답을 못 받았어도 줄이 사실을 보이게 한다.
  // 이력 줄(`job-events`)과 `W-55` 의 원본 목록(`originals-summary`)도 함께 다시 부른다.
  // ⭐ **다시 부르기를 기다린다**(`await`). 그동안 이 동작이 `isPending` 이라 줄이 지우기 전 장 수를 그리지 않는다.
  const deleteMutation = useMutation({
    mutationFn: () => deleteJobOriginals(job!.id),
    onSuccess: (res) => notify(deleteResultMessage(res)),
    onError: (error) => notify(deleteErrorMessage(error)),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["job-originals", job?.id] }),
        queryClient.invalidateQueries({ queryKey: ["job-events", job?.id] }),
        queryClient.invalidateQueries({ queryKey: ["originals-summary"] }),
      ]);
    },
  });
  // ⭐ **되돌리기는 판을 만들지 않는다**(DATA_DICTIONARY 51.2). 그래서 판 목록은 다시 부르지 않고, 편집
  // 상태를 들고 있는 작업(`["job", id]`)과 이력만 다시 부른다.
  const revertMutation = useMutation({
    mutationFn: (target: { version: string; versionId: string }) => revertToVersion(job!.id, target.versionId),
    onSuccess: (res, target) => notify(revertResultMessage(target.version, res)),
    onError: (error) => notify(revertErrorMessage(error)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["job", job?.id] });
      void queryClient.invalidateQueries({ queryKey: ["job-events", job?.id] });
    },
  });
  const busy = deleteMutation.isPending || revertMutation.isPending;

  // ── 타임라인 ─────────────────────────────────────────────────────────────
  //
  // 서버는 최신순으로 준다. **웹은 오래된 것이 위다**(목업 `W-50`. 앱과의 엇갈림은 D-12).
  // 문구를 여기서 다시 짓지 않는다. `무슨 일` 은 `JobEvent.summary` 그대로다.
  const serverEvents = [...(eventsQuery.data?.items ?? [])].reverse();
  const versionItems = versionsQuery.data?.items ?? [];
  // 연도는 열 단위로 켠다(PRD 7.5.13 규칙 4). 타임라인과 판 목록이 한 화면이라 같은 판정을 쓴다.
  const year = needsYear([
    ...serverEvents.map((e) => e.occurredAt),
    ...versionItems.map((v) => v.createdAt),
  ]);

  const timeline: TimelineRow[] = real
    ? serverEvents.map((event) => {
        // **`0` 은 돈이 나간 것이 아니다.** 이미 있던 결과를 그대로 준 회차가 `0` 으로 온다.
        const cost =
          event.costAmount !== null && event.costAmount > 0 ? formatKrw(event.costAmount) : undefined;
        // 출처 낱말은 앱 · 알림과 같은 shared 표에서 온다(PRD `7.5.9`. 따로 적으면 한쪽만 바뀐다).
        const word = activityWord(event.source);
        return {
          key: event.id,
          when: formatDateTime(event.occurredAt, { year }),
          what: event.summary,
          ...(cost ? { cost } : {}),
          who: event.detail ? `${word} · ${event.detail}` : word,
          // 강조는 둘뿐이다. 돈이 나간 줄과 판이 만들어진 줄(PRD 7.9. 앱 `A-12` 와 같다).
          accent: Boolean(cost) || event.versionNo !== null,
        };
      })
    : [];

  // ── 판 ───────────────────────────────────────────────────────────────────
  //
  // 서버가 번호 큰 것을 위에 두고 내려준다. 맨 위가 지금 판이라 `다시 받기` 가 서고, 나머지는
  // `되돌리기` 다(목업 `W-50`).
  //
  // ⭐ **못 만든 장이 결판나기 전에는 받는 길을 그리지 않는다.** `W-40` 이 같은 동안 `전체 받기`
  // 를 숨기는 이유(`Export.tsx` 의 `hidesBundle`)가 여기에도 그대로 선다. 여기서 받게 두면
  // 그 화면이 닫아 둔 길이 이력 화면으로 열린다. 판정은 같은 shared 함수에서 온다.
  const decline = view ? exportDeclineView(view) : null;
  const hidesBundle = decline?.stage === "ASK" || decline?.stage === "SETTLED";
  /** ⭐ **서버가 받기 기간이 지났다고 판정한 작업**(`resultReceive.state === "CLOSED"`. #218 · PRD
   *  `7.6.14c` ①). `다시 받기` 는 `<a href>` 라 409 를 화면이 받지 못한다. 그래서 이 값 하나로 처음부터
   *  끈다. ⛔ `UNKNOWN` 은 켜지 않는다. ⛔ 결과 주소 `null` 로 대신하지 않는다. */
  const receiveClosed = isResultReceiveClosed(view);

  const versions: VersionRow[] = real
    ? versionItems.map((v, index) => ({
        key: v.id,
        versionId: v.id,
        name: `v${v.versionNo}`,
        at: formatDateTime(v.createdAt, { year }),
        // 꼬리표(`note`)는 승인에서 언제나 비어 있다. 있을 때만 붙인다(DATA_DICTIONARY 13.5).
        sub: [`파일 ${v.fileCount}개`, `블록 ${v.blockCount}종`, v.note].filter(Boolean).join(" · "),
        current: index === 0,
        ...(index === 0 && job && !hidesBundle ? { bundleHref: versionBundleUrl(job.id, v.id) } : {}),
      }))
    : [];

  // 배경 지운 장 수는 셸이 든 값이다. 원본 장 수는 **남은 원본**(`originalCount`)이다. 올린 장 전체
  // (`job.total`)로 적으면 지운 뒤에도 `원본 8장` 이 남아 거짓이 된다.
  // ⛔ **모르는 수를 그리지 않는다.** 불러오는 중 · 실패(다시 부르다 실패하면 React Query 가 옛 값을 들고
  // 있다) · 지우기가 끝나고 다시 부르는 중에는 줄을 그리지 않는다.
  const originalsKnown = originalsQuery.isSuccess && !deleteMutation.isPending;
  const originals = originalsKnown ? originalsLine(originalsQuery.data, job?.done ?? 0) : null;
  /** 원본을 한 장도 받은 적 없는 작업은 `originalsLine` 이 `null` 이다. 그때는 제목째 그리지 않는다. */
  const originalsSection = real && (originalsKnown ? originals !== null : !originalsQuery.isError);

  const summary =
    real && eventsQuery.isSuccess && versionsQuery.isSuccess
      ? `이력 ${timeline.length}줄 · 내보낸 판 ${versions.length}개`
      : undefined;

  const loading = real && (eventsQuery.isPending || versionsQuery.isPending);
  // 원본 현황을 못 읽은 것도 같은 배너가 말한다. 줄을 비워 두면 셀러는 원본이 없어진 줄 안다.
  const failed = real && (eventsQuery.isError || versionsQuery.isError || originalsQuery.isError);

  return (
    <>
      <PageHead frame="W-50" title="이력" right={summary} />

      {/* 줄이 없는 것과 못 읽은 것은 다른 일이다. 빈 타임라인으로 두면 셀러는 기록이 사라진
          줄 안다. 문장은 앱 `A-12` 의 것과 같다. */}
      {failed ? (
        <LimitBanner>이력을 불러오지 못했습니다. 연결을 확인하고 다시 열어 주세요.</LimitBanner>
      ) : receiveClosed ? (
        // 받기 기간이 지난 작업. 불러오기 실패와 같은 자리이고, 둘이 함께면 불러오기 실패가 먼저다.
        <LimitBanner>
          <span className="l1">{RESULT_RECEIVE_EXPIRED_COPY.head}</span>
          <span className="l2">{RESULT_RECEIVE_EXPIRED_COPY.reason}</span>
        </LimitBanner>
      ) : null}

      <div className="hist" style={{ marginTop: 16 }}>
        <div>
          <h4 className="hist-h">무슨 일이 있었나</h4>
          {loading ? (
            <div role="status" aria-live="polite" style={{ display: "grid", gap: 9 }}>
              <Skeleton width="72%" />
              <Skeleton width="88%" />
              <Skeleton width="64%" />
            </div>
          ) : (
            <ul className="tl">
              {timeline.map((row) => (
                <li key={row.key} className={row.accent ? "acc" : undefined}>
                  <span className="when mono">{row.when}</span>
                  <span className="what">
                    {row.what}
                    {row.cost ? (
                      <>
                        {" "}
                        <span className="cost mono">{row.cost}</span>
                      </>
                    ) : null}
                  </span>
                  <span className="who">{row.who}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h4 className="hist-h">내보낸 판</h4>
          {real && versionsQuery.isSuccess && versions.length === 0 && !receiveClosed ? (
            <p className="hint">
              <b>아직 확정한 판이 없습니다.</b> 결과 확인에서 <b>좋아요 · 이 판으로 확정</b>을
              누르면 그때 받을 파일이 만들어집니다.
            </p>
          ) : null}
          <div className="vers">
            {versions.map((v) => (
              <div className={`ver${v.current ? " cur" : ""}`} key={v.key}>
                <span>
                  <span className="vname mono">
                    {v.name} · {v.at}
                  </span>
                  <span className="vsub">{v.sub}</span>
                </span>
                {v.current && real ? (
                  /* **`fetch` 가 아니라 최상위 이동이다.** 브라우저가 파일로 받아야 하므로
                     `<a href>` 로 건다(`versionBundleUrl` 머리말. `W-40` 과 같은 주소). */
                  receiveClosed ? (
                    // 링크를 숨기지 않고 꺼진 버튼으로 둔다. 묶음 숨김(`hidesBundle`)과 겹쳐도 이 상태가 이긴다.
                    <Button
                      label="다시 받기"
                      variant="ghost"
                      small
                      disabled
                      disabledReason={RESULT_RECEIVE_EXPIRED_COPY.disabledReason}
                    />
                  ) : v.bundleHref ? (
                    <a className="btn ghost small" href={v.bundleHref}>
                      다시 받기
                    </a>
                  ) : null
                ) : (
                  <Button
                    label={v.current ? "다시 받기" : "되돌리기"}
                    variant="ghost"
                    small
                    disabled={!v.current && busy}
                    onClick={() =>
                      v.current
                        ? notify("내보낸 파일이 아직 만들어지지 않아 다시 받을 것이 없습니다.")
                        : setDialog({ kind: "revert", version: v.name, versionId: v.versionId })
                    }
                  />
                )}
              </div>
            ))}
          </div>
          <p className="hint" style={{ marginTop: 8 }}>
            {/* ⭐ 2026-10-25 `design-manager` 자문. 서버는 다섯 축을 되돌리고, 무엇이 바뀔지는 **실행해 봐야
                안다**(미리보기 API 없음). 그래서 옛 「무엇이 되돌아가는지 확인 창에서 먼저 보여줍니다」라는
                약속을 걷었다. 바뀐 축은 결과 알림이 말한다(`revertResultMessage`). */}
            <span className="l1">
              <b>“되돌리기”는 확인 창에서 한 번 더 묻습니다.</b> 사진 순서·상품명·블록 구성·배경·스펙표
              내용이 그 판의 값으로 돌아갑니다.
            </span>
            <span className="l2">
              {/* `금액이 들지 않습니다` 에서 바꿨다. 용어 표가 같은 사실을 부르는 두 꼴을
                  `더 내실 금액이 없습니다` 하나로 합쳤다(PRD `7.5.9` 판정 9. 목업 `W-50`).
                  끝 문장: 되돌리기는 판을 만들지 않아 맨 위 판과 `다시 받기` 는 그대로다(51.2). */}
              <b>되돌리기에는 더 내실 금액이 없습니다.</b> 배경 지우기를 다시 하지 않습니다. 되돌린 값은
              다음에 내보낼 때 새 판이 됩니다.
            </span>
          </p>

          {/* 현황을 못 읽었으면 줄을 그리지 않는다. 못 읽은 사실은 맨 위 불러오기 실패 배너가 말한다. */}
          {originalsSection ? (
            <h4 className="hist-h" style={{ marginTop: 20 }}>
              원본 사진
            </h4>
          ) : null}
          {originals ? (
            <div className="vers">
              <div className="ver">
                <span>
                  <span className="vname mono">{originals.name}</span>
                  {/* ⭐ 보관 기간은 올린 날부터 30일(2026-09-23 사용자 확정. 목업 `W-50`). 원본이 있어야 못
                      만든 장을 다시 만들 수 있다(`A-31f`). 남은 날은 서버가 SQL 로 셈한 `daysLeft` 다. 화면이
                      다시 세지 않는다(shared `originals-api.ts` 머리말). 문장은 `originalsLine`. */}
                  <span className="vsub">{originals.sub}</span>
                </span>
                {/* 남은 원본이 없으면 버튼을 숨긴다. 기다리면 풀리는 막힘이 아니라 끝난 상태다. */}
                {originals.showDelete ? (
                  <Button
                    label="지우기"
                    variant="ghost"
                    small
                    disabled={busy || originals.blockedReason !== null}
                    {...(originals.blockedReason ? { disabledReason: originals.blockedReason } : {})}
                    onClick={() => setDialog({ kind: "delete", count: originalsQuery.data?.originalCount ?? 0 })}
                  />
                ) : null}
              </div>
            </div>
          ) : originalsSection && !originalsKnown ? (
            <Skeleton width="72%" />
          ) : null}
        </div>
      </div>

      {dialog?.kind === "revert" ? (
        <ConfirmDialog
          title={`${dialog.version} 판으로 되돌릴까요?`}
          body={
            <>
              {/* 다섯 축. 서버가 되돌리는 것(`RevertChangedPart`)과 한 벌이고 순서는 `W-30` 이다. */}
              <b>{REVERT_ALL_PARTS}</b>이 그 판의 값으로 돌아갑니다. 원본 사진과 배경 지운 결과는
              그대로 있고, <b>되돌리기에는 더 내실 금액이 없습니다.</b> 배경 지우기를 다시 하지 않습니다.
            </>
          }
          confirmLabel="되돌리기"
          onConfirm={() => {
            revertMutation.mutate({ version: dialog.version, versionId: dialog.versionId });
            setDialog(null);
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === "delete" ? (
        <ConfirmDialog
          title={`원본 사진 ${dialog.count}장을 지울까요?`}
          body={
            <>
              {/* ⭐ 가운데 문장은 2026-10-13 에 바뀌었다(PRD `7.6.14c` 딸린 정정. 원본 지우기 확인 창). 옛 「이미 내보낸
                  파일은 남지만」은 결과에 기간이 없던 때의 말이다. 문장은 shared 에 한 벌이다.
                  첫 문장의 버튼 이름은 2026-10-17 `되돌리기` 로 바뀌었다(PRD 7.5.1a). 지금 화면에 없는 옛 이름
                  「이 판으로 되돌리기」를 인용하지 않는다(2026-10-25 `design-manager` 자문). */}
              지우면 <b>이력의 “되돌리기”가 함께 죽습니다.</b>{" "}
              {EXPORTED_FILES_AFTER_ORIGINAL_DELETE.ONE_JOB} <b>청구 내역은 지워지지 않습니다.</b>
            </>
          }
          confirmLabel="지우기"
          destructive
          onConfirm={() => {
            deleteMutation.mutate();
            setDialog(null);
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}

      {toast ? <Toast message={toast} /> : null}

      {/* ⛔ **설계 메모 상자를 걷었다** (2026-10-04 사용자 지시). 「확인 창까지는 실제로
          동작합니다」 배너, 「이력이 있어야 셋이 성립합니다」 콜아웃, 「이 화면에서 아직 못 합니다」,
          타임라인 아래 「정렬 방향이 앱과 엇갈립니다」(미정 배지)는 설계자에게 하는 말이었다.
          결과와 거절 사유는 누른 자리의 알림(`notify`)이 말한다. */}
    </>
  );
}
